import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import BookingsService from '@/shared/types/bookings/service.type';

import BookingContactForm from '../types/bookingContactForm.type';
import transformBookingServiceToContactForm from '../utils/transformBookingServiceToContactForm';
import AddContactFormList from './AddContactFormList';

type AddContactsDialogProps = {
  bookingService: BookingsService;
  errors?: string[];
  loading?: boolean;
  onClose: () => void;
  proceed: (contacts: BookingContactForm[]) => void;
};

const BookingServiceAddContactsDialog: FC<AddContactsDialogProps> = ({
  bookingService,
  errors,
  loading,
  onClose,
  proceed,
}) => {
  const { t } = useTranslation(['bookingsServices', 'common']);

  const [contactsForms, setContactsForms] = useState<
    ({ id: string } & BookingContactForm)[]
  >([]);
  const [warning, setWarning] = useState<string>('');
  const [formErrors, setFormErrors] = useState<
    {
      id: string;
      booking_contact_id: string;
      calendar_ids: string;
    }[]
  >([]);

  const onValidateContacts = () => {
    const errors = contactsForms.filter(
      (contact) =>
        !contact.booking_contact_id || contact.calendar_ids.length === 0,
    );

    setFormErrors(
      errors.map((error) => ({
        id: error.id,
        booking_contact_id: !error.booking_contact_id
          ? t('bookingsServices:booking_contact_id_required')
          : '',
        calendar_ids:
          error.calendar_ids.length === 0
            ? t('bookingsServices:calendar_ids_required')
            : '',
      })),
    );

    return errors.length === 0;
  };

  const onProceedWithContacts = () => {
    if (!loading) {
      const isFormValid = onValidateContacts();

      if (!isFormValid) {
        setWarning(t('bookingsServices:addBookingServiceContactsWarning'));
        return;
      } else {
        setWarning('');
      }

      proceed(
        contactsForms.map((contact) => ({
          booking_contact_id: contact.booking_contact_id,
          calendar_ids: contact.calendar_ids,
        })),
      );
    }
  };

  if (errors && errors.length > 0) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('bookingsServices:addBookingServiceContacts'),
          content: (
            <Stack spacing={2} useFlexGap>
              {t('bookingsServices:addBookingServiceContactsError')}
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('bookingsServices:addBookingServiceContacts'),
        content: (
          <Stack spacing={2}>
            {warning && <Alert severity='warning'>{warning}</Alert>}
            <AddContactFormList
              contactsList={transformBookingServiceToContactForm(
                bookingService,
              )}
              contactsListErrors={formErrors}
              getContacts={(contacts) => {
                setContactsForms(contacts);
              }}
            />
          </Stack>
        ),
        actions: [
          {
            label: t('common:close'),
            onClick: onClose,
          },
          {
            label: t('bookingsServices:addContacts'),
            variant: 'contained',
            onClick: () => {
              onProceedWithContacts();
            },
          },
        ],
      }}
    />
  );
};

export default BookingServiceAddContactsDialog;
